/**
 * GlobalLeaderboard — unified points-based ranking + live activity feed.
 * Shows the universal "who's doing what" view across the whole app.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Trophy, Activity, Flame, Crown, Star, Loader2, Award, Info,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { key: "daily", label: "Today" },
  { key: "weekly", label: "Week" },
  { key: "monthly", label: "Month" },
  { key: "all", label: "All Time" },
];

const PODIUM_COLORS = {
  1: { bg: "from-amber-500/20 to-amber-900/5", text: "text-amber-400", border: "border-amber-500/40" },
  2: { bg: "from-zinc-300/20 to-zinc-700/5", text: "text-zinc-300", border: "border-zinc-400/40" },
  3: { bg: "from-orange-700/20 to-orange-900/5", text: "text-orange-500", border: "border-orange-700/40" },
};

const timeAgo = (iso) => {
  const t = new Date(iso);
  const diff = (Date.now() - t.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const GlobalLeaderboard = () => {
  const [period, setPeriod] = useState("weekly");
  const [board, setBoard] = useState(null);
  const [feed, setFeed] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [pointValues, setPointValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRules, setShowRules] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, f, m, v] = await Promise.all([
        axios.get(`${API}/points/leaderboard?period=${period}&limit=25`, { headers: headers() }),
        axios.get(`${API}/points/feed?limit=20`, { headers: headers() }),
        axios.get(`${API}/points/me`, { headers: headers() }),
        axios.get(`${API}/points/values`, { headers: headers() }),
      ]);
      setBoard(b.data);
      setFeed(f.data?.feed || []);
      setMyStats(m.data);
      setPointValues(v.data?.values || {});
    } catch (e) {
      console.error("global leaderboard load failed", e);
    } finally {
      setLoading(false);
    }
  }, [period]);

  // eslint-disable-next-line
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const podium = (board?.rankings || []).slice(0, 3);
  const rest = (board?.rankings || []).slice(3);

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6 lg:p-8" data-testid="global-leaderboard-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto space-y-6"
      >
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="w-4 h-4 text-amber-400" />
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-400">
                Global Standings
              </p>
            </div>
            <h1 className="text-5xl md:text-6xl font-black uppercase text-white tracking-tight leading-none">
              Leaderboard
            </h1>
            <p className="text-xs text-white/40 mt-2 font-mono">
              Every workout · every drill · every PR — earns points
            </p>
          </div>
          <button
            onClick={() => setShowRules((v) => !v)}
            className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 hover:text-amber-400 inline-flex items-center gap-1.5"
            data-testid="show-rules-btn"
          >
            <Info className="w-3 h-3" /> How points work
          </button>
        </div>

        {/* My Stats Strip */}
        {myStats && (
          <div className="grid grid-cols-3 gap-px bg-white/[0.06]" data-testid="my-points-strip">
            <div className="bg-[#0A0A0A] p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-white/40">Your Points</p>
              <p className="text-2xl font-black text-white mt-1" data-testid="my-points-total">
                {myStats.total_points || 0}
              </p>
            </div>
            <div className="bg-[#0A0A0A] p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-white/40">Global Rank</p>
              <p className="text-2xl font-black text-amber-400 mt-1" data-testid="my-global-rank">
                {myStats.global_rank ? `#${myStats.global_rank}` : "—"}
              </p>
              <p className="text-[10px] font-mono text-white/30">of {myStats.total_athletes || 0}</p>
            </div>
            <div className="bg-[#0A0A0A] p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-white/40">Activities</p>
              <p className="text-2xl font-black text-white mt-1">
                {myStats.by_activity?.reduce((a, x) => a + x.count, 0) || 0}
              </p>
            </div>
          </div>
        )}

        {/* Period filter */}
        <div className="flex gap-px bg-white/[0.06]">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`flex-1 py-2.5 text-[10px] font-mono uppercase tracking-widest ${
                period === p.key ? "bg-amber-500 text-black" : "bg-[#0A0A0A] text-white/50 hover:text-white"
              }`}
              data-testid={`period-${p.key}-btn`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left: Rankings */}
            <div className="lg:col-span-2 space-y-4">
              {/* Podium top 3 */}
              {podium.length > 0 && (
                <div className="grid grid-cols-3 gap-2" data-testid="podium">
                  {podium.map((r) => {
                    const c = PODIUM_COLORS[r.rank];
                    const Icon = r.rank === 1 ? Crown : r.rank === 2 ? Award : Star;
                    return (
                      <div
                        key={r.user_id}
                        className={`bg-gradient-to-b ${c.bg} border ${c.border} p-4 text-center`}
                        data-testid={`podium-${r.rank}`}
                      >
                        <Icon className={`w-5 h-5 mx-auto ${c.text}`} />
                        <p className={`text-3xl font-black mt-1 ${c.text}`}>#{r.rank}</p>
                        <p className="text-xs text-white mt-1 truncate font-bold">
                          {r.name}{r.is_me && <span className="text-amber-400 ml-1">(you)</span>}
                        </p>
                        <p className="text-xs font-mono text-white/60 mt-1">{r.points} pts</p>
                        <p className="text-[10px] font-mono text-white/30">{r.activities} acts</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Rest of leaderboard */}
              <div className="space-y-px bg-white/[0.06]" data-testid="rankings-list">
                {rest.length === 0 && podium.length === 0 ? (
                  <div className="bg-[#0A0A0A] p-8 text-center text-white/40 text-xs font-mono uppercase tracking-widest">
                    No activity yet for this period
                  </div>
                ) : (
                  rest.map((r) => (
                    <div
                      key={r.user_id}
                      className={`flex items-center justify-between p-3 ${
                        r.is_me ? "bg-amber-500/10 border-l-2 border-amber-500" : "bg-[#0A0A0A]"
                      }`}
                      data-testid={`rank-${r.rank}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-base font-black text-white/30 w-8 text-center">
                          {r.rank}
                        </span>
                        <div>
                          <p className="text-sm text-white font-bold">
                            {r.name}{r.is_me && <span className="text-amber-400 ml-1 text-xs">(you)</span>}
                          </p>
                          <p className="text-[10px] font-mono text-white/40">{r.activities} activities</p>
                        </div>
                      </div>
                      <p className="text-base font-black text-amber-400 font-mono">{r.points}</p>
                    </div>
                  ))
                )}
              </div>

              {/* Self row if outside top 25 */}
              {board?.my_rank && !board.rankings.some((r) => r.is_me) && (
                <div className="bg-amber-500/10 border-l-2 border-amber-500 p-3 flex items-center justify-between">
                  <span className="text-sm text-white">
                    #{board.my_rank.rank} {board.my_rank.name} (you)
                  </span>
                  <span className="text-base font-black text-amber-400 font-mono">{board.my_rank.points}</span>
                </div>
              )}
            </div>

            {/* Right: Live Activity Feed */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-400" />
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-orange-400">
                  Live Activity Feed
                </p>
              </div>
              <div className="space-y-px bg-white/[0.06]" data-testid="activity-feed">
                {feed.length === 0 ? (
                  <div className="bg-[#0A0A0A] p-6 text-center text-white/30 text-xs">
                    No activity yet
                  </div>
                ) : (
                  feed.map((f, i) => (
                    <div key={i} className="bg-[#0A0A0A] p-2.5" data-testid={`feed-item-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white truncate">
                            <span className="font-bold">{f.name}</span>{" "}
                            <span className="text-white/60">{f.label}</span>
                          </p>
                          <p className="text-[10px] font-mono text-white/30 mt-0.5">
                            {timeAgo(f.created_at)}
                          </p>
                        </div>
                        <span className="text-xs font-mono font-black text-amber-400 whitespace-nowrap">
                          +{f.points}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Points rules modal */}
        {showRules && pointValues && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setShowRules(false)}
            data-testid="points-rules-modal"
          >
            <div
              className="bg-[#0A0A0A] border border-white/[0.06] max-w-md w-full p-6 space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-black uppercase text-white">Earn Points By</h2>
                <button onClick={() => setShowRules(false)} className="text-white/40 hover:text-white text-xs uppercase">
                  Close
                </button>
              </div>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {Object.entries(pointValues).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm p-2 border-b border-white/[0.04]">
                    <span className="text-white/80 capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="text-amber-400 font-black font-mono">+{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default GlobalLeaderboard;
